import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getVideos, createVideo, updateVideo, deleteVideo, getYoutubeThumbnailUrl, type Video } from '../../services/api/videos';

export default function VideosManagerPage() {
  const { t } = useTranslation();
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title_en: '', title_th: '', youtube_url: '', is_featured: false });
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    getVideos()
      .then(setVideos)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title_en.trim() || !form.youtube_url.trim()) return;
    setAdding(true);
    try {
      await createVideo({ ...form, sort_order: videos.length });
      setVideos(await getVideos());
      setForm({ title_en: '', title_th: '', youtube_url: '', is_featured: false });
    } catch (err) {
      console.error(err);
    } finally {
      setAdding(false);
    }
  }

  async function handleToggleFeatured(v: Video) {
    await updateVideo(v.id, { is_featured: !v.is_featured });
    setVideos(await getVideos());
  }

  async function handleDelete(id: string) {
    if (!confirm('Remove this video?')) return;
    await deleteVideo(id);
    setVideos(await getVideos());
  }

  if (loading) return <p className="text-[var(--color-text-muted)]">{t('common.loading')}</p>;

  return (
    <div className="admin-page">
      <h1 className="admin-page__title">{t('admin.videos')}</h1>
      <form onSubmit={handleAdd} className="admin-form-row">
        <div className="admin-form-group" style={{ minWidth: '140px' }}>
          <input
            placeholder="Title (EN)"
            value={form.title_en}
            onChange={(e) => setForm((f) => ({ ...f, title_en: e.target.value }))}
          />
        </div>
        <div className="admin-form-group" style={{ minWidth: '140px' }}>
          <input
            placeholder="Title (TH)"
            value={form.title_th}
            onChange={(e) => setForm((f) => ({ ...f, title_th: e.target.value }))}
          />
        </div>
        <div className="admin-form-group" style={{ minWidth: '200px' }}>
          <input
            placeholder="YouTube URL"
            value={form.youtube_url}
            onChange={(e) => setForm((f) => ({ ...f, youtube_url: e.target.value }))}
          />
        </div>
        <label className="admin-checkbox-label">
          <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm((f) => ({ ...f, is_featured: e.target.checked }))} />
          {t('admin.featured')}
        </label>
        <button type="submit" disabled={adding} className="admin-btn">{t('admin.addVideo')}</button>
      </form>
      <div className="admin-video-grid">
        {videos.map((v) => (
          <div key={v.id} className="admin-video-card">
            <img src={getYoutubeThumbnailUrl(v.youtube_url)} alt="" className="admin-video-card__thumb" />
            <div className="admin-video-card__body">
              <p className="admin-video-card__title">{v.title_en}</p>
              <div className="admin-video-card__actions">
                <button type="button" onClick={() => handleToggleFeatured(v)} className="admin-btn admin-btn--sm">
                  {v.is_featured ? t('admin.unfeature') : t('admin.featured')}
                </button>
                <button type="button" onClick={() => handleDelete(v.id)} className="admin-btn admin-btn--danger admin-btn--sm">{t('common.delete')}</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {videos.length === 0 && <p className="admin-page__empty">{t('admin.noVideos')}</p>}
    </div>
  );
}
